import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NbDialogService, NbLayoutScrollService } from "@nebular/theme";
import { replace, remove, clone } from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { UserService } from "../../../@core/services/user.service";
import { convertTimestamp } from "../../../@core/utils/convertTimestamps.service";

import QnA, { QnAReply } from "../../../models/qna.model";
import ReportSpam from "../../../models/report-spam.model";
import { NotificationService } from "../../../services/notifications.service";
import { QnAService } from "../../../services/qna.service";
import { DialogYesNoPromptComponent } from "../../dialog-yesno-prompt/dialog-yesno-prompt.component";

@Component({
  selector: "ngx-view-replies",
  styleUrls: ["./view-replies.component.scss"],
  templateUrl: "./view-replies.component.html",
})
export class ViewQnARepliesComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  @Input() qna = new QnA();
  @Input() mode?: string = "course";
  @Output() onClose: EventEmitter<any> = new EventEmitter();

  replyForm: FormGroup;
  submitted = false;
  loading = false;

  qnaReplies?: QnAReply[] = [];
  reply?: QnAReply = new QnAReply();

  uid = "anonymous";
  pageSize = 3;
  cursor: any;
  done = false;

  posX = 0;
  posY = 0;

  constructor(
    private fb: FormBuilder,
    private qnaService: QnAService,
    public userService: UserService,
    private notiService: NotificationService,
    private dialogService: NbDialogService,
    private scrollService: NbLayoutScrollService
  ) {}

  ngOnInit() {
    this.reply.content = "";

    if (this.qna != "") {
      this.getReplies();
    }

    this.replyForm = this.fb.group({
      content: Validators.required,
    });

    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user && user.uid != this.uid) {
          this.uid = user.uid;
        } else {
          this.uid = "anonymous";
        }
      });

    this.scrollService
      .getPosition()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.posX = data.x;
        this.posY = data.y;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMore() {
    this.pageSize = 12;
    this.getReplies();
  }

  createReply() {
    this.loading = true;
    this.submitted = true;

    this.reply.qnaId = this.qna.id;
    this.reply.qnaTitle = this.qna.title;
    this.reply.qnaDescription = this.qna.description;
    this.reply.privacy = this.qna.privacy;

    this.qnaService
      .createReply(this.reply)
      .then((value) => {
        this.qna.noReply++;
        const newReply = clone(value);
        this.qnaReplies = [newReply, ...this.qnaReplies];

        if (this.qna.createdBy != this.uid) {
          //Send notification to tutor
          this.notiService.create(
            "Trả lời",
            `qnas/${this.qna.id}/replies`,
            value.id,
            this.qna.courseId,
            "",
            this.qna.title,
            this.qna.createdBy,
            `/qna/${this.qna.id}`,
            this.qna.courseId
          );
        }

        this.reply = new QnAReply();
        this.reply.content = "";
        this.loading = false;
        this.submitted = false;
      })
      .catch((err) => {
        this.loading = false;
        this.submitted = false;
      });
  }

  deleteReply(item) {
    this.dialogService
      .open(DialogYesNoPromptComponent, {
        context: {
          title: "Bạn có chắc xóa nội dung này?",
        },
      })
      .onClose.subscribe((value) => {
        if (value) {
          this.submitted = true;
          this.loading = true;
          this.submitted = true;

          this.qnaService
            .deleteReply(item)
            .then((result) => {
              this.qna.noReply--;

              remove(this.qnaReplies, (e) => {
                return e.id == item.id;
              });
              this.loading = false;
              this.submitted = false;
            })
            .catch((err) => {
              this.loading = false;
              this.submitted = false;
            });
        }
      });
  }

  //Editing
  content = "";
  onEditReply(item: QnAReply) {
    item.editing = true;
    this.content = item.content;
  }

  editReply(item: QnAReply) {
    item.loading = true;
    this.submitted = true;

    this.qnaService
      .updateReply(item.qnaId, item.id, this.content)
      .then((data) => {
        item.content = this.content;
        item.editing = false;

        item.loading = false;
        this.submitted = false;
      })
      .catch((err) => {
        item.editing = false;
        item.loading = false;
        this.submitted = false;
      });
  }

  cancelReply(item: QnAReply) {
    item.editing = false;
  }

  report(item: ReportSpam) {
    remove(this.qnaReplies, (e) => {
      return e.id == item.refId;
    });
  }

  onNewReply(item: QnAReply) {
    //
    if (item) {
      this.reply.toReplyId = item.id;
      this.reply.toUid = item.createdBy;
      this.reply.toAuthor = item.author;
    } else {
      this.reply = new QnAReply();
    }

    this.scrollService.scrollTo(this.posX, this.posY);
  }

  closeReplies() {
    this.onClose.emit(true);
  }

  private getReplies(): void {
    this.loading = true;

    this.qnaService
      .getReplies(this.qna.id, this.qna.privacy, this.pageSize, this.cursor)
      .then((querySnapshot) => {
        if (querySnapshot.docs.length > 0) {
          this.cursor = querySnapshot.docs[querySnapshot.docs.length - 1];

          querySnapshot.forEach((doc) => {
            let data = new QnA();
            data = doc.data();
            data.id = doc.id;
            data.createdOn = convertTimestamp(doc.data().createdOn);
            this.qnaReplies.push(data);
          });
        } else {
          this.cursor = null;
          this.done = true;
        }

        this.loading = false;
      })
      .catch((error) => {});
  }
}
